import { useMemo } from "react";
import colors from "tailwindcss/colors";

import { DEFAULT_BASE_CHART_CONFIG } from "../constants";
import { transformDataForChart, createChartSeries } from "../utils";
import { ProcessedData, USAGE_TYPE_KEYS, UsageTypeKey } from "../types";

import {
  formatNumberWithSuffix,
  formatPlanQuotaCompact,
  isChartDataEmpty,
  calculateProjectedOveragePrice,
  shouldShowLimitLine,
} from "./utils";

import { formatSpacedCategoryAxisLabel } from "@/utils/dateAxisCategoryLabels";
import { OrganizationUsageTotal } from "@/types/organization-usage";
import { useOrganizationSubscriptionData } from "@/providers";

export const useChartOptions = ({
  usageType,
  processedData,
  chartType,
  limit,
  totalOrganizationUsage,
}: {
  usageType: UsageTypeKey;
  processedData: ProcessedData;
  chartType: "cumulative" | "periodic";
  limit?: number | string;
  totalOrganizationUsage?: OrganizationUsageTotal;
}) => {
  const { subscription } = useOrganizationSubscriptionData();
  const chartData = useMemo(
    () =>
      transformDataForChart(
        usageType,
        processedData[usageType],
        processedData.workspaceInfo,
        chartType,
      ),
    [usageType, processedData, chartType],
  );

  const chartSeries = useMemo(
    () => createChartSeries(processedData[usageType], processedData.workspaceInfo),
    [usageType, processedData],
  );

  const chartOptions = useMemo(() => {
    const baseConfig = JSON.parse(JSON.stringify(DEFAULT_BASE_CHART_CONFIG));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    baseConfig.axes[1].label.formatter = (params: any) => formatNumberWithSuffix(params.value);

    baseConfig.axes[0].label.formatter = (params: { value: unknown; index: number }) =>
      formatSpacedCategoryAxisLabel({
        datapointCount: chartData.length,
        value: params.value,
        index: params.index,
      });

    const yAxisIndex = baseConfig.axes.findIndex(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (axis: any) => axis.type === "number",
    );

    if (yAxisIndex !== -1) {
      if (limit && typeof limit === "number" && shouldShowLimitLine(chartType, usageType)) {
        let maxDataValue = 0;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        chartData.forEach((point: any) => {
          let stackedSum = 0;
          Object.keys(point).forEach(key => {
            if (key !== "datetime" && key !== "rawDatetime" && key !== "isProjected") {
              const value = point[key];
              if (typeof value === "number") {
                stackedSum += value;
              }
            }
          });
          if (stackedSum > maxDataValue) {
            maxDataValue = stackedSum;
          }
        });

        const yMax = Math.max(limit, maxDataValue) * 1.1;

        baseConfig.axes[yAxisIndex] = {
          ...baseConfig.axes[yAxisIndex],
          type: "number" as const,
          min: 0,
          max: yMax,
          crossLines: [
            {
              type: "line" as const,
              value: limit,
              stroke: colors.gray[500],
              strokeWidth: 2,
              lineDash: [5, 5],
              label: {
                text: `Plan Quota (${formatPlanQuotaCompact(limit)})`,
                fontSize: 11,
                color: colors.gray[500],
                position: "top-left" as const,
              },
            },
          ],
        };
      }
    }

    return {
      ...baseConfig,
      data: chartData,
      series: chartSeries,
    };
  }, [chartData, chartSeries, limit, chartType, usageType]);

  const overageRates = useMemo(
    () => ({
      request_logs: subscription?.overage_rate_requests,
      workflow_nodes: subscription?.overage_rate_workflow_nodes,
      eval_cells: subscription?.overage_rate_eval_cells,
    }),
    [subscription],
  );

  const isOverLimit = useMemo(
    () =>
      typeof limit === "number" &&
      limit <= (totalOrganizationUsage?.[USAGE_TYPE_KEYS[usageType]] || 0),
    [limit, totalOrganizationUsage, usageType],
  );

  const projectedPrice = useMemo(
    () =>
      calculateProjectedOveragePrice(
        totalOrganizationUsage?.[USAGE_TYPE_KEYS[usageType]] ?? undefined,
        limit,
        overageRates[USAGE_TYPE_KEYS[usageType] as keyof typeof overageRates] ?? undefined,
      ),
    [totalOrganizationUsage, usageType, limit, overageRates],
  );

  const total = useMemo(
    () => totalOrganizationUsage?.[USAGE_TYPE_KEYS[usageType]],
    [totalOrganizationUsage, usageType],
  );

  const isEmpty = useMemo(() => isChartDataEmpty(chartData), [chartData]);

  return {
    chartData,
    chartSeries,
    limit,
    chartOptions,
    isOverLimit,
    projectedPrice,
    total,
    isEmpty,
  };
};
